/**
 * @file Checklist template modal submit owner.
 */

import {
    addDoc,
    collection,
    doc,
    serverTimestamp,
    updateDoc,
} from "https://www.gstatic.com/firebasejs/12.19.0/firebase-firestore.js";

import { db } from "../../app/firebase/firebase.js";
import { appData, state } from "../../app/state/state.js";

const ENTITY_TYPE_CONFIG_KEYS = {
    task: "taskTypes",
    patient: "patientTypes",
    project: "projectTypes",
    asset: "assetTypes",
    lab: "labTypes",
    maintenance: "maintenanceTypes",
};

function normalizeArrayField(data, formData, fieldName) {
    const value = data[fieldName];
    if (Array.isArray(value)) {
        return value.map((entry) => String(entry)).filter(Boolean);
    }

    if (typeof value === "string" && value.trim().startsWith("[")) {
        try {
            const parsed = JSON.parse(value);
            if (Array.isArray(parsed)) {
                return parsed.map((entry) => String(entry)).filter(Boolean);
            }
        } catch (error) {
            console.warn(`Failed to parse ${fieldName} array value:`, error);
        }
    }

    return formData.getAll(fieldName)
        .map((entry) => String(entry))
        .filter(Boolean);
}

function getEffectiveEntities(allowedEntities = []) {
    const allowed = new Set(allowedEntities);
    if (allowed.has("all")) {
        return Object.keys(ENTITY_TYPE_CONFIG_KEYS);
    }
    return Object.keys(ENTITY_TYPE_CONFIG_KEYS)
        .filter((entity) => allowed.has(entity));
}

function getAllAvailableLinkedTypeIds(allowedEntities = []) {
    const seen = new Set();
    getEffectiveEntities(allowedEntities).forEach((entity) => {
        const configKey = ENTITY_TYPE_CONFIG_KEYS[entity];
        const types = appData?.config?.[configKey] || [];
        types.forEach((type) => {
            if (!type?.id || type.isActive === false) {return;}
            seen.add(String(type.id));
        });
    });
    return Array.from(seen);
}

function normalizeLinkedTypesForSave(linkedTypes = [], allowedEntities = []) {
    const selected = linkedTypes.map((value) => String(value)).filter(Boolean);
    const selectedSet = new Set(selected);
    const allAvailable = getAllAvailableLinkedTypeIds(allowedEntities);

    if (allAvailable.length === 0) {
        return [];
    }

    const includesEveryAvailableType = allAvailable.every((value) =>
        selectedSet.has(value));
    const hasOnlyAvailableTypes = selected.every((value) =>
        allAvailable.includes(value));

    return includesEveryAvailableType && hasOnlyAvailableTypes ? [] : selected;
}

/**
 * Saves a checklist template form payload.
 * @param {object} params Submit context.
 * @param {object} params.data Form payload.
 * @param {FormData} params.formData Raw form data.
 * @param {string} params.id Existing template ID.
 * @returns {Promise<void>}
 */
export async function saveChecklistTemplateForm({ data, formData, id }) {
    data.allowedEntities = normalizeArrayField(data, formData, "allowedEntities");
    data.linkedTypes = normalizeArrayField(data, formData, "linkedTypes");
    data.linkedTypes = normalizeLinkedTypesForSave(
        data.linkedTypes,
        data.allowedEntities,
    );
    data.isManual = formData.get("isManual") === "on";
    data.sortOrder = parseInt(data.sortOrder, 10) || 1;

    if (data.itemsJson) {
        try {
            data.items = JSON.parse(data.itemsJson);
        } catch (error) {
            console.error("Failed to parse checklist items:", error);
            data.items = {};
        }
        delete data.itemsJson;
    }

    if (!id) {
        data.createdBy = state.currentUser.uid;
        data.createdAt = serverTimestamp();
    }
    data.updatedBy = state.currentUser.uid;
    data.updatedAt = serverTimestamp();

    if (id) {
        await updateDoc(doc(db, "checklist_templates", id), data);
    } else {
        await addDoc(collection(db, "checklist_templates"), data);
    }
}
